import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useAuth } from "../../context/AuthContext";
import { API_URLS } from "../../config";
import { apiRequest } from "../../utils/apiRequest";
import { DEFAULT_CURRENCY_RATES } from "../../utils/currencyRates";
import AccessDenied from "../../Components/AccessDenied";
import { useToast } from "../../Components/Toast";
import {
  FiTrash2, FiChevronUp, FiChevronDown, FiDollarSign,
  FiCheck, FiX, FiChevronLeft, FiChevronRight,
} from "react-icons/fi";
import formatDate from "../../utils/formatDate";

const MONTH_NAMES = ["January","February","March","April","May","June","July","August","September","October","November","December"];
const fmtMonth = (yyyymm) => {
  const [y, m] = yyyymm.split('-');
  return `${MONTH_NAMES[parseInt(m, 10) - 1]} ${y}`;
};

// Currencies that can be entered here — INR is always fixed at 1, so it's excluded.
const CURRENCY_CODES = Object.keys(DEFAULT_CURRENCY_RATES).filter(c => c !== 'INR');

// Local calendar "today" as YYYY-MM-DD — NOT new Date().toISOString().slice(0,10),
// which reports the UTC date and would show yesterday's date for part of the day in
// timezones ahead of UTC (e.g. IST, midnight-5:30am).
const todayInput = () => {
  const d = new Date();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${mm}-${dd}`;
};
const EMPTY_FORM = { code: CURRENCY_CODES[0] || '', date: todayInput(), rate: '' };

// A dated history of currency conversion rates — every entry here is a rate that was
// in effect on a given date, kept forever (never overwritten), so past rates stay
// available for reference/audit. Rate editing lives only here; the Customer Invoice
// page just reads today's rate (read-only "Rates" popup + a link back to this page).
//
// The table is laid out as one row PER DATE with one column PER CURRENCY (a matrix),
// so every currency's rate for every date is visible at a glance — click any cell to
// add or edit that currency's rate for that date.
const CurrencyRateMaster = () => {
  const { token } = useAuth();
  const toast = useToast();

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [denied, setDenied] = useState(null);
  const [isTableCollapsed, setIsTableCollapsed] = useState(false);

  const [modal, setModal] = useState(null); // "add" | "edit"
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const fetchRows = useCallback(async () => {
    setLoading(true);
    const result = await apiRequest(API_URLS.FINANCE_CURRENCY_RATES, {}, token);
    if (!result) return;
    if (result.denied) { setDenied(result); setLoading(false); return; }
    if (result.ok) setRows(result.data.rows || []);
    setLoading(false);
  }, [token]);

  useEffect(() => { fetchRows(); }, [fetchRows]);

  // Pivot the flat { code, date, rate }[] list into one row per date, with each
  // currency's entry (if any) keyed by code — the matrix the table renders from.
  const dateRows = useMemo(() => {
    const byDate = new Map();
    for (const r of rows) {
      const key = (r.date || '').slice(0, 10);
      if (!key) continue;
      if (!byDate.has(key)) byDate.set(key, { date: key, cells: {} });
      byDate.get(key).cells[r.code] = r;
    }
    return [...byDate.values()].sort((a, b) => b.date.localeCompare(a.date));
  }, [rows]);

  // Unique months (YYYY-MM) sorted newest-first derived from dateRows.
  const months = useMemo(() => {
    const set = new Set(dateRows.map(dr => dr.date.slice(0, 7)));
    return [...set].sort((a, b) => b.localeCompare(a));
  }, [dateRows]);

  // Index into `months` — 0 = most recent. Reset to 0 whenever months list changes.
  const [selectedMonthIdx, setSelectedMonthIdx] = useState(0);
  useEffect(() => { setSelectedMonthIdx(0); }, [months.length]);

  const selectedMonth = months[selectedMonthIdx] || null;

  // Rows for the currently-selected month only.
  const visibleRows = useMemo(
    () => selectedMonth ? dateRows.filter(dr => dr.date.startsWith(selectedMonth)) : dateRows,
    [dateRows, selectedMonth]
  );

  const initialFormSnapshotRef = useRef(null);

  // Empty cell clicked — prefill the currency/date that cell represents.
  const openCellAdd = (code, date) => {
    const next = { code, date, rate: '' };
    setFormData(next);
    setEditingId(null);
    initialFormSnapshotRef.current = JSON.stringify(next);
    setModal("add");
  };
  // Filled cell clicked — edit that entry.
  const openCellEdit = (entry) => {
    const next = { code: entry.code, date: (entry.date || '').slice(0, 10), rate: entry.rate };
    setFormData(next);
    setEditingId(entry._id);
    initialFormSnapshotRef.current = JSON.stringify(next);
    setModal("edit");
  };
  const closeModal = () => { setModal(null); setEditingId(null); setFormData(EMPTY_FORM); };

  const isFormDirty = () => initialFormSnapshotRef.current !== null && JSON.stringify(formData) !== initialFormSnapshotRef.current;
  const requestCloseModal = () => {
    if (isFormDirty()) {
      toast.confirm(
        "You have unsaved changes. Do you want to save before closing?",
        () => handleSave(),
        () => closeModal()
      );
    } else {
      closeModal();
    }
  };

  const handleFormChange = e => setFormData(f => ({ ...f, [e.target.name]: e.target.value }));

  const handleSave = async (e) => {
    if (e) e.preventDefault();
    if (!formData.code || !formData.date || formData.rate === '') {
      toast.error("Currency, date and rate are all required.");
      return;
    }
    setSaving(true);
    const isEdit = modal === "edit" && editingId;
    const url = isEdit
      ? `${API_URLS.FINANCE_CURRENCY_RATES}/entries/${editingId}`
      : `${API_URLS.FINANCE_CURRENCY_RATES}/entries`;
    const method = isEdit ? "PUT" : "POST";
    const result = await apiRequest(url, { method, body: formData }, token);
    setSaving(false);
    if (result?.ok) {
      toast.success("Rate saved successfully!");
      closeModal();
      fetchRows();
    } else toast.error(result?.data?.message || "Failed");
  };

  const handleDelete = () => {
    if (!editingId) return;
    toast.deleteConfirm(`Delete the ${formData.code} rate for ${formatDate(formData.date)}?`, async () => {
      setDeleting(true);
      const res = await apiRequest(`${API_URLS.FINANCE_CURRENCY_RATES}/entries/${editingId}`, { method: "DELETE" }, token);
      setDeleting(false);
      if (res?.ok) {
        toast.success("Deleted.");
        closeModal();
        fetchRows();
      } else toast.error(res?.data?.message || "Failed to delete.");
    });
  };

  if (denied) return (
    <div className="vendor-page">
      <div className="management-card">
        <AccessDenied message={denied.message} required={denied.required} />
      </div>
    </div>
  );

  return (
    <div className="vendor-page">
      {/* ── Table Card ── */}
      <div className="management-card">
        <div className="management-header" onClick={() => setIsTableCollapsed(!isTableCollapsed)}>
          <div className="header-title">
            <FiDollarSign size={18} />
            <h2>Currency Rate Master</h2>
            <span className="discipline-badge">{months.length} {months.length === 1 ? 'month' : 'months'}</span>
          </div>
          <div>{isTableCollapsed ? <FiChevronDown /> : <FiChevronUp />}</div>
        </div>

        {!isTableCollapsed && (
          <div className="vendor-table-container">
            {loading && rows.length === 0 ? <div style={{ padding: 20 }}>Loading...</div> : (
              <table className="vendor-table">
                <thead>
                  <tr>
                    <th>DATE</th>
                    {CURRENCY_CODES.map(code => <th key={code} style={{ textAlign: 'center' }}>{code}</th>)}
                  </tr>
                </thead>
                <tbody>
                  {visibleRows.length === 0 ? (
                    <tr><td colSpan={CURRENCY_CODES.length + 1} style={{ textAlign: "center", padding: 40 }}>No rate entries for this month</td></tr>
                  ) : visibleRows.map((dr) => (
                    <tr key={dr.date}>
                      <td>{formatDate(dr.date)}</td>
                      {CURRENCY_CODES.map(code => {
                        const entry = dr.cells[code];
                        const isAuto = entry && !entry.updatedBy;
                        return (
                          <td
                            key={code}
                            onClick={() => entry ? openCellEdit(entry) : openCellAdd(code, dr.date)}
                            title={entry ? (isAuto ? 'Auto (daily sync) — click to edit' : `Updated by ${entry.updatedBy.fullName} — click to edit`) : 'Click to add a rate'}
                            style={{
                              textAlign: 'center', cursor: 'pointer',
                              color: entry ? (isAuto ? '#888' : '#1a2a4d') : '#ccc',
                              fontStyle: isAuto ? 'italic' : 'normal',
                              fontWeight: entry && !isAuto ? 600 : 400,
                            }}
                          >
                            {entry ? entry.rate : '+'}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            )}

            {/* Month navigation bar — below the table */}
            {months.length > 0 && (
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 0', borderTop: '1px solid #eee' }}>
                <button
                  onClick={() => setSelectedMonthIdx(i => Math.min(i + 1, months.length - 1))}
                  disabled={selectedMonthIdx >= months.length - 1}
                  style={{ background: 'none', border: '1px solid #ddd', borderRadius: 4, padding: '4px 10px', cursor: selectedMonthIdx >= months.length - 1 ? 'not-allowed' : 'pointer', color: '#555', display: 'flex', alignItems: 'center', gap: 4, opacity: selectedMonthIdx >= months.length - 1 ? 0.4 : 1 }}
                >
                  <FiChevronLeft size={14} /> Prev
                </button>

                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <span style={{ fontWeight: 700, fontSize: 15, color: '#1a2a4d' }}>
                    {selectedMonth ? fmtMonth(selectedMonth) : '—'}
                  </span>
                  <span style={{ fontSize: 12, color: '#888' }}>
                    ({visibleRows.length} {visibleRows.length === 1 ? 'day' : 'days'})
                  </span>
                </div>

                <button
                  onClick={() => setSelectedMonthIdx(i => Math.max(i - 1, 0))}
                  disabled={selectedMonthIdx <= 0}
                  style={{ background: 'none', border: '1px solid #ddd', borderRadius: 4, padding: '4px 10px', cursor: selectedMonthIdx <= 0 ? 'not-allowed' : 'pointer', color: '#555', display: 'flex', alignItems: 'center', gap: 4, opacity: selectedMonthIdx <= 0 ? 0.4 : 1 }}
                >
                  Next <FiChevronRight size={14} />
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {modal && (
        <div className="vendor-modal-overlay" onClick={requestCloseModal}>
          <div className="vendor-modal" onClick={e => e.stopPropagation()} style={{ width: "85%", maxWidth: "480px" }}>
            <div style={{ background: "#1a2a4d", color: "#fff", padding: "12px 30px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <span style={{ fontWeight: 600 }}>{modal.toUpperCase()} CURRENCY RATE</span>
              <button onClick={requestCloseModal} style={{ background: "none", border: "none", color: "#fff", fontSize: 20, cursor: "pointer" }}><FiX /></button>
            </div>
            <div style={{ padding: 30 }}>
              <form onSubmit={handleSave}>
                {modal === 'edit' ? (
                  // Edit mode — currency & date are fixed to the cell that was clicked
                  // (shown as plain text, not editable); only the rate can be changed.
                  <div className="vendor-view-grid">
                    <div className="vendor-view-field">
                      <label className="vendor-view-label">Currency</label>
                      <div style={{ fontWeight: 600, padding: '8px 0' }}>{formData.code}</div>
                    </div>
                    <div className="vendor-view-field">
                      <label className="vendor-view-label">Date</label>
                      <div style={{ fontWeight: 600, padding: '8px 0' }}>{formatDate(formData.date)}</div>
                    </div>
                    <div className="vendor-view-field vendor-full">
                      <label className="vendor-view-label">Rate (1 {formData.code} = this many INR) *</label>
                      <input type="number" step="0.0001" min="0" name="rate" className="cust-input" value={formData.rate} onChange={handleFormChange} required autoFocus />
                    </div>
                  </div>
                ) : (
                  <div className="vendor-view-grid">
                    <div className="vendor-view-field">
                      <label className="vendor-view-label">Currency *</label>
                      <select name="code" className="cust-input" value={formData.code} onChange={handleFormChange} required>
                        {CURRENCY_CODES.map(c => <option key={c} value={c}>{c}</option>)}
                      </select>
                    </div>
                    <div className="vendor-view-field">
                      <label className="vendor-view-label">Date *</label>
                      <input type="date" name="date" className="cust-input" value={formData.date} onChange={handleFormChange} required />
                    </div>
                    <div className="vendor-view-field vendor-full">
                      <label className="vendor-view-label">Rate (1 {formData.code || 'unit'} = this many INR) *</label>
                      <input type="number" step="0.0001" min="0" name="rate" className="cust-input" value={formData.rate} onChange={handleFormChange} required />
                    </div>
                  </div>
                )}

                <div className="form-footer" style={{ marginTop: 30, textAlign: "right", display: "flex", justifyContent: modal === 'edit' ? "space-between" : "flex-end", gap: 10 }}>
                  {modal === 'edit' && (
                    <button type="button" className="action-btn delete-btn" disabled={deleting} onClick={handleDelete}><FiTrash2 /> Delete</button>
                  )}
                  <div style={{ display: 'flex', gap: 10 }}>
                    <button type="submit" className="save-btn" disabled={saving}><FiCheck /> Save</button>
                    <button type="button" className="cancel-btn" onClick={requestCloseModal}>Cancel</button>
                  </div>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CurrencyRateMaster;
